import React, { useEffect, useState } from "react";
import {
Box,
Card,
Typography,
TextField,
Button,
Stack,
Snackbar,
Alert,
CircularProgress,
InputAdornment,
IconButton
} from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

import { Colors } from "../utils/colors";
import { adminFetch } from "../services/adminFetch";
import { useNavigate } from "react-router-dom";
import AdminPageContainer from "../components/AdminPageContainer";
import { adminPageSx, contentCardSx } from "../utils/adminResponsive";

const initialForm = {
  email: "",
  role: "",
  first_name: "",
  last_name: "",
  phone: "",
  current_password: "",
  new_password: ""
};

const profileFieldGridSx = {
  display: "grid",
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  gridTemplateColumns: {
    xs: "1fr",
    md: "repeat(2, minmax(0, 1fr))"
  },
  gap: 2
};

const profileFieldSx = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  background: "#fff",
  borderRadius: 1,
  "& .MuiInputBase-root": {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box"
  }
};

export default function Profile() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);

      try {
        const data = await adminFetch("/api/admin/profile", {
          method: "GET"
        });

        if (data?.status && data.admin) {
          setFormData((prev) => ({
            ...prev,
            email: data.admin.email || "",
            role: data.admin.role || "",
            first_name: data.admin.first_name || "",
            last_name: data.admin.last_name || "",
            phone: data.admin.phone || ""
          }));
        }
      } catch (error) {
        console.log(error);
      }

      setLoading(false);
    };

    loadProfile();
  }, []);

  const handleSave = async () => {
    setSaving(true);

    try {
      const data = await adminFetch("/api/admin/profile", {
        method: "PUT",
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          current_password: formData.current_password,
          new_password: formData.new_password
        })
      });

      if (!data?.status) {
        showSnackbar(data?.message || "Failed to update profile", "error");
        setSaving(false);
        return;
      }

      showSnackbar("Profile updated successfully");
      setFormData((prev) => ({
        ...prev,
        current_password: "",
        new_password: ""
      }));
    } catch (error) {
      console.log(error);
      showSnackbar("Server error", "error");
    }

    setSaving(false);
  };

  return (
    <Box
      sx={{
        ...adminPageSx
      }}
    >
      <AdminPageContainer>
        <Typography variant="h5" fontWeight={700} sx={{ color: Colors.accent, mb: 3 }}>
          My Profile
        </Typography>

        <Card sx={{ p: { xs: 2, sm: 3, lg: 4 }, maxWidth: 760, mx: "auto", borderRadius: 3, ...contentCardSx }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2.5}>
              <Box sx={profileFieldGridSx}>
                <TextField label="Email" value={formData.email} fullWidth disabled sx={profileFieldSx} />
                <TextField label="Role" value={formData.role} fullWidth disabled sx={profileFieldSx} />
              </Box>

              <Box sx={profileFieldGridSx}>
                <TextField
                  label="First Name"
                  value={formData.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                  fullWidth
                  sx={profileFieldSx}
                />
                <TextField
                  label="Last Name"
                  value={formData.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                  fullWidth
                  sx={profileFieldSx}
                />
              </Box>

              <TextField
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                fullWidth
                sx={profileFieldSx}
              />

              <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                Change Password
              </Typography>

              <TextField
                label="Current Password"
                type={showCurrentPassword ? "text" : "password"}
                value={formData.current_password}
                onChange={(e) => updateField("current_password", e.target.value)}
                fullWidth
                sx={profileFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowCurrentPassword((prev) => !prev)} edge="end">
                        {showCurrentPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                value={formData.new_password}
                onChange={(e) => updateField("new_password", e.target.value)}
                fullWidth
                sx={profileFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end">
                        {showNewPassword ? <VisibilityOffIcon /> : <RemoveRedEyeIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Typography
                sx={{ fontSize: 13, color: Colors.accent, fontWeight: 600, cursor: "pointer", overflowWrap: "anywhere" }}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot Password
              </Typography>

              <Button
                variant="contained"
                sx={{
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  bgcolor: Colors.accent,
                  color: "#003300",
                  fontWeight: 700
                }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Save Changes"}
              </Button>
            </Stack>
          )}
        </Card>
      </AdminPageContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
